import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth, isJsonContentType, providesList } from '../api';
import HelperFunctions from '../../pages/global/HelperFunctions';
import { getEnv } from '../../config/Env';
import axios from 'axios';

const baseUrl = getEnv('GUARD_BASE_URL');
const uriPrefix = '/api';

const guardApiQuery = fetchBaseQuery({
    baseUrl,
    isJsonContentType,
    responseHandler: 'content-type',
});

export const guardApi = createApi({
    reducerPath: 'guardApi',
    baseQuery: (args, api, extraOptions) => baseQueryWithReauth(args, api, extraOptions, guardApiQuery),
    tagTypes: [
        'GuardEntity',
        'GuardAlias',
        'GuardAudit',
        'NotificationUser',
        'User',
        'GuardEntityReports',
        'GuardReport',
        'GuardEntityAudits',
    ],
    endpoints: (builder) => ({
        connect: builder.mutation({
            query: (body) => {
                return {
                    url: '/connect',
                    method: 'POST',
                    body,
                };
            },
        }),
        getGuardEntities: builder.query({
            query: ({ token, params }) => ({
                url: HelperFunctions.prepareQuery(`${uriPrefix}/guard_entities`, params),
                headers: prepareHeaders(token),
            }),
            transformResponse: (response) => response['member'],
            providesTags: (result) => providesList(result, 'GuardEntity'),
        }),
        getGuardAudits: builder.query({
            query: ({ uri, token, params }) => ({
                url: HelperFunctions.prepareQuery(`${uri}/guard_audits`, params),
                headers: prepareHeaders(token),
            }),
            providesTags: (_result, _error, arg) => [
                { type: arg.type, id: arg.uri },
                { type: 'GuardEntity', id: arg.guardEntityUri },
                { type: 'GuardEntityAudits', id: arg.guardEntityUri },
            ],
        }),
        getGuardAliases: builder.query({
            query: ({ uri, token, params }) => ({
                url: HelperFunctions.prepareQuery(`${uri}/guard_aliases`, params),
                headers: prepareHeaders(token),
            }),
            providesTags: (result, error, arg) => {
                const tags = providesList(result['member'], 'GuardAlias');

                return tags.concat([{ type: 'GuardEntity', id: arg.uri }]);
            },
        }),
        getGuardReports: builder.query({
            query: ({ uri, token }) => ({
                url: `${uri}/guard_reports`,
                headers: prepareHeaders(token),
            }),
            providesTags: (result, error, arg) => {
                const tags = providesList(result['member'], 'GuardReport');

                return tags.concat([{ type: 'GuardEntityReports', id: arg.uri }]);
            },
        }),
        createGuardReport: builder.mutation({
            query: ({ uri, token }) => ({
                url: `${uri}/report`,
                method: 'POST',
                headers: prepareHeaders(token, 'POST'),
                body: {},
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: 'GuardEntityReports', id: arg.uri }],
        }),
        getGuardEntity: builder.query({
            query: ({ id, token }) => ({
                url: `${uriPrefix}/guard_entities/${id}`,
                headers: prepareHeaders(token),
            }),
            providesTags: (result) => [{ type: 'GuardEntity', id: result['@id'] }],
        }),
        createGuardEntity: builder.mutation({
            query: ({ body, token }) => ({
                url: `${uriPrefix}/guard_entities`,
                method: 'POST',
                headers: prepareHeaders(token),
                body,
            }),
            invalidatesTags: () => [{ type: 'GuardEntity', id: 'LIST' }],
        }),
        updateGuardEntity: builder.mutation({
            query: ({ uri, body, token }) => ({
                url: uri,
                method: 'PATCH',
                headers: prepareHeaders(token, 'PATCH'),
                body: JSON.stringify(body),
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'GuardEntity', id: arg.uri }],
        }),
        updateAuditable: builder.mutation({
            query: ({ uri, body, token }) => ({
                url: uri,
                method: 'PATCH',
                headers: prepareHeaders(token, 'PATCH'),
                body: JSON.stringify(body),
            }),
            invalidatesTags: (result) => [{ type: result['@type'], id: result['@id'] }],
        }),
        createGuardAudit: builder.mutation({
            query: ({ uri, body, token }) => ({
                url: `${uri}/audit`,
                method: 'POST',
                headers: prepareHeaders(token, 'POST'),
                body: JSON.stringify(body),
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: 'GuardEntityAudits', id: arg.uri }],
        }),
        createGuardAlias: builder.mutation({
            query: ({ uri, body, token }) => ({
                url: `${uriPrefix}/guard_aliases`,
                method: 'POST',
                headers: prepareHeaders(token, 'POST'),
                body: JSON.stringify(body),
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'GuardEntity', id: arg.uri },
                {
                    type: 'GuardAlias',
                    id: 'LIST',
                },
            ],
        }),
        createEntityToken: builder.mutation({
            query: ({ uri, type, body = {}, token }) => ({
                url: `${uri}/token`,
                method: 'POST',
                headers: prepareHeaders(token, 'POST'),
                body: JSON.stringify(body),
            }),
            invalidatesTags: (result, error, arg) => [{ type: arg['type'], id: arg['uri'] }],
        }),
        getUser: builder.query({
            query: ({ token }) => ({
                url: `${uriPrefix}/user`,
                headers: prepareHeaders(token),
            }),
            providesTags: (result) => [{ type: 'User', id: result['@id'] }],
        }),
        getNotificationUsers: builder.query({
            query: ({ id, token }) => ({
                url: `${uriPrefix}/users/${id}/notification_users`,
                headers: prepareHeaders(token),
            }),
            transformResponse: (response) => response['member'],
            providesTags: (result) => providesList(result, 'NotificationUser'),
        }),
        updateNotificationUsers: builder.mutation({
            query: ({ userId, body, token }) => ({
                url: `${uriPrefix}/users/${userId}`,
                method: 'PUT',
                headers: prepareHeaders(token, 'PUT'),
                body: JSON.stringify(body),
            }),
            invalidatesTags: () => [{ type: 'NotificationUser', id: 'LIST' }],
        }),
        sendAuditNotifications: builder.mutation({
            query: ({ uri, token }) => ({
                url: `${uri}/notifications`,
                method: 'POST',
                headers: prepareHeaders(token, 'POST'),
                body: {},
            }),
        }),
    }),
});

export const {
    useConnectMutation,
    useGetGuardEntitiesQuery,
    useGetGuardEntityQuery,
    useCreateGuardEntityMutation,
    useUpdateGuardEntityMutation,
    useGetGuardAuditsQuery,
    useGetGuardAliasesQuery,
    useGetGuardReportsQuery,
    useCreateGuardAuditMutation,
    useCreateGuardReportMutation,
    useCreateGuardAliasMutation,
    useUpdateAuditableMutation,
    useCreateEntityTokenMutation,
    useGetUserQuery,
    useGetNotificationUsersQuery,
    useUpdateNotificationUsersMutation,
    useSendAuditNotificationsMutation,
} = guardApi;

export function downloadReport(uri, token) {
    const axiosApiInstance = axios.create();

    return axiosApiInstance.post(`${baseUrl}${uri}/download`, undefined, {
        responseType: 'blob',
        headers: prepareHeaders(token, 'POST'),
    });
}

function prepareHeaders(token, method) {
    let headers = {
        authorization: `Bearer ${token}`,
    };

    if (method === 'PATCH') {
        headers['content-type'] = 'application/merge-patch+json';
    }

    if (method === 'POST' || method === 'PUT') {
        headers['content-type'] = 'application/json';
    }

    return headers;
}
